// src/services/localStorageService.js

/**
 * Helper functions for working with localStorage in the SipNScore Customer app
 */

// Keys used for localStorage
const KEYS = {
  DISCLAIMER_ACCEPTED: "sipnscore_disclaimer_accepted",
  QR_DATA: "sipnscore_qr_data",
  USER_DATA: "sipnscore_user_data",
  SESSION_TOKEN: "sipnscore_session_token",
};

/**
 * Check if the user has accepted the disclaimer
 * @returns {boolean} True if the disclaimer has been accepted
 */
export const getAcceptedDisclaimer = () => {
  return localStorage.getItem(KEYS.DISCLAIMER_ACCEPTED) === "true";
};

/**
 * Set whether the user has accepted the disclaimer
 * @param {boolean} accepted - Whether the disclaimer is accepted
 */
export const setAcceptedDisclaimer = (accepted) => {
  localStorage.setItem(KEYS.DISCLAIMER_ACCEPTED, accepted ? "true" : "false");
};

const QR_DATA_MAX_AGE_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Save QR code data to localStorage with timestamp for staleness check
 * @param {Object} data - The QR code data object
 */
export const saveQRData = (data) => {
  if (!data) return;
  const wrapped = { ...data, _savedAt: Date.now() };
  localStorage.setItem(KEYS.QR_DATA, JSON.stringify(wrapped));
};

/**
 * Get QR code data from localStorage. Returns null if data is stale (>30 min).
 * @returns {Object|null} The QR code data object or null if not found/stale
 */
export const getQRData = () => {
  try {
    const raw = localStorage.getItem(KEYS.QR_DATA);
    if (!raw) return null;
    const data = JSON.parse(raw);
    // Expire cached QR data after 30 minutes
    if (data._savedAt && Date.now() - data._savedAt > QR_DATA_MAX_AGE_MS) {
      localStorage.removeItem(KEYS.QR_DATA);
      return null;
    }
    // Strip internal field before returning
    const { _savedAt, ...cleanData } = data;
    return cleanData;
  } catch (error) {
    console.error("Error parsing QR data from localStorage:", error);
    return null;
  }
};

/**
 * Clear QR code data from localStorage
 */
export const clearQRData = () => {
  localStorage.removeItem(KEYS.QR_DATA);
};

/**
 * Save user data to localStorage
 * @param {Object} data - The user data object
 */
export const saveUserData = (data) => {
  if (!data) return;
  localStorage.setItem(KEYS.USER_DATA, JSON.stringify(data));
};

/**
 * Get user data from localStorage
 * @returns {Object|null} The user data object or null if not found
 */
export const getUserData = () => {
  try {
    const data = localStorage.getItem(KEYS.USER_DATA);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error("Error parsing user data from localStorage:", error);
    return null;
  }
};

/**
 * Clear user data from localStorage
 */
export const clearUserData = () => {
  localStorage.removeItem(KEYS.USER_DATA);
};

/**
 * Save session token to sessionStorage (URL masking)
 * @param {string} token - The session token
 */
export const saveSessionToken = (token) => {
  if (!token) return;
  sessionStorage.setItem(KEYS.SESSION_TOKEN, token);
};

/**
 * Get session token from sessionStorage
 * @returns {string|null} The session token or null
 */
export const getSessionToken = () => {
  return sessionStorage.getItem(KEYS.SESSION_TOKEN);
};

/**
 * Clear session token from sessionStorage
 */
export const clearSessionToken = () => {
  sessionStorage.removeItem(KEYS.SESSION_TOKEN);
};

/**
 * Clear all SipNScore data from localStorage
 */
export const clearAllData = () => {
  localStorage.removeItem(KEYS.DISCLAIMER_ACCEPTED);
  localStorage.removeItem(KEYS.QR_DATA);
  localStorage.removeItem(KEYS.USER_DATA);
  sessionStorage.removeItem(KEYS.SESSION_TOKEN);
};

/**
 * Check if localStorage is available in the browser
 * @returns {boolean} True if localStorage is available
 */
export const isLocalStorageAvailable = () => {
  try {
    const testKey = "__storage_test__";
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
};
